import React, { useEffect, useState } from "react";
import { createGallery } from "../../services/galleryService";
import { getAllPackages } from "../../services/paymentService";
import Button from "./Button";
import Input from "./Input";
import { toast } from "react-toastify";

const CreateGalleryModal = ({ eventId, onClose, onCreated }) => {
  const [form, setForm] = useState({
    name: "",
    albumName: "",
    IsPremium: false,
    IsWatermark: false,
    IsWatermarkAlbum: false,
    MakeAlbumsPremium: false,
    PackageId: "",
  });
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [packagesLoaded, setPackagesLoaded] = useState(false);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  useEffect(() => {
    if (!form.IsPremium || packagesLoaded || packagesLoading) return;

    const loadPackages = async () => {
      setPackagesLoading(true);
      try {
        const res = await getAllPackages();
        const raw = Array.isArray(res.data?.Result)
          ? res.data.Result
          : Array.isArray(res.data)
            ? res.data
            : [];
        setPackages(raw.filter((pkg) => pkg?.IsActive !== false));
        setPackagesLoaded(true);
      } catch (err) {
        toast.error(err.response?.data?.Message || "Failed to load package prices.");
      } finally {
        setPackagesLoading(false);
      }
    };

    loadPackages();
  }, [form.IsPremium, packagesLoaded, packagesLoading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.warning("Gallery name is required."); return; }
    if (!form.albumName.trim()) { toast.warning("Album name is required."); return; }
    if (form.IsPremium && !form.PackageId) {
      toast.warning("Please select a package price for premium gallery.");
      return;
    }

    setLoading(true);
    try {
      const res = await createGallery({
        eventId: Number(eventId),
        name: form.name.trim(),
        isPremium: form.IsPremium,
        packageId: form.IsPremium ? Number(form.PackageId) : null,
        MakeAlbumsPremium: form.MakeAlbumsPremium,
        IsWatermark: form.IsWatermark,
        AlbumName: form.albumName.trim(),
        IsWatermarkAlbum: form.IsWatermarkAlbum,
      });
      toast.success(res.data?.Message || "Gallery created!");
      onCreated(res.data?.Result);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.Message || "Failed to create gallery.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>Create New Gallery</h3>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <Input
            label="Gallery Name *"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="e.g. Night Photos"
            required
          />
          <Input
            label="Default Album Name *"
            value={form.albumName}
            onChange={(e) => set("albumName", e.target.value)}
            placeholder="e.g. Main Album"
            required
          />

          <div style={styles.checkboxGroup}>
            <label style={styles.checkboxLabel}>
              <input type="checkbox" checked={form.IsWatermark} onChange={(e) => set("IsWatermark", e.target.checked)} />
              <span>Watermark on Gallery</span>
            </label>
            <label style={styles.checkboxLabel}>
              <input type="checkbox" checked={form.IsWatermarkAlbum} onChange={(e) => set("IsWatermarkAlbum", e.target.checked)} />
              <span>Watermark on Album</span>
            </label>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={form.IsPremium}
                onChange={(e) => {
                  const checked = e.target.checked;
                  set("IsPremium", checked);
                  if (!checked) {
                    set("PackageId", "");
                  }
                }}
              />
              <span>Premium Gallery</span>
            </label>
            {form.IsPremium && (
              <div style={styles.selectWrap}>
                <label style={styles.selectLabel}>Package Price *</label>
                <select
                  value={form.PackageId}
                  onChange={(e) => set("PackageId", e.target.value)}
                  required={form.IsPremium}
                  style={styles.selectInput}
                >
                  <option value="">Select package price</option>
                  {packages.map((pkg) => (
                    <option key={pkg.Id} value={pkg.Id}>
                      {Number(pkg.DefaultPrice || 0).toFixed(2)} {pkg.Currency}
                    </option>
                  ))}
                </select>
                {packagesLoading && <div style={styles.selectHint}>Loading package prices...</div>}
              </div>
            )}
            {/*
            <label style={styles.checkboxLabel}>
              <input type="checkbox" checked={form.MakeAlbumsPremium} onChange={(e) => set("MakeAlbumsPremium", e.target.checked)} />
              <span>Make Albums Premium</span>
            </label>
            */}
          </div>

          <div style={styles.actions}>
            <Button variant="secondary" onClick={onClose} type="button">Cancel</Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Creating..." : "Create Gallery"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const overlayStyle = {
  position: "fixed", inset: 0,
  background: "rgba(0,0,0,0.45)",
  display: "flex", alignItems: "center", justifyContent: "center",
  zIndex: 1000,
};

const modalStyle = {
  background: "#fff",
  borderRadius: 16,
  padding: "28px 32px",
  width: "100%",
  maxWidth: 460,
  boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
};

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 },
  title: { margin: 0, fontSize: 18, fontWeight: 700, color: "#1e1e2d" },
  closeBtn: { background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#888" },
  checkboxGroup: { display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 },
  checkboxLabel: { display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: "#444", cursor: "pointer" },
  selectWrap: { marginTop: -2, marginBottom: 2 },
  selectLabel: { display: "block", marginBottom: 6, fontWeight: 500, fontSize: 14, color: "#444" },
  selectInput: {
    width: "100%",
    padding: "10px 14px",
    border: "1.5px solid #e5e7eb",
    borderRadius: 8,
    fontSize: 14,
    boxSizing: "border-box",
    outline: "none",
    background: "#fafafa",
  },
  selectHint: { marginTop: 6, fontSize: 12, color: "#888" },
  actions: { display: "flex", justifyContent: "flex-end", gap: 12 },
};

export default CreateGalleryModal;
